import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  RateType,
  TariffGroupType,
  TariffType,
  TariffZoneType,
  useFuelProfilesQuery,
  useMeQuery,
  useServicesQuery,
  useShallowContactsQuery,
  useTariffZoneGroupsQuery,
  useVehicleTypesQuery,
} from "../../../../generated/graphql";
import { useCrownEnv } from "../../env/env-context";
import { BulkDefaultsPanel } from "./bulk-defaults-panel";
import { BulkGrid } from "./bulk-grid";
import type { BulkBinding, BulkRow } from "./bulk-types";
import { fromCsv, toCsv } from "./manifest-csv";
import {
  MANIFEST_COLUMNS,
  recordToResolution,
  rowToRecord,
} from "./manifest-mapping";
import {
  defaultNameFromFile,
  makeRowId,
  useBulkTariffImport,
} from "./use-bulk-tariff-import";

const PROD_CONFIRM_PHRASE = "PROD";
const MANIFEST_FILENAME = "crown-tariff-manifest.csv";

const styles = {
  container: { padding: 3 },
  section: { marginTop: 3 },
  dropzone: {
    border: "2px dashed",
    borderColor: "text.secondary",
    borderRadius: 1,
    padding: 3,
    textAlign: "center",
    cursor: "pointer",
  },
  header: { display: "flex", alignItems: "center", gap: 1, marginBottom: 1 },
  actions: { display: "flex", alignItems: "center", gap: 2, marginTop: 2 },
  confirmInput: { maxWidth: 240 },
  danger: { border: "2px solid", borderColor: "error.main", borderRadius: 1 },
  manifestButtons: { display: "flex", gap: 1, marginBottom: 1 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Builds the initial shared-default binding for a fresh bulk session, mirroring
 * the single-tariff wizard's defaults.
 *
 * @returns A default binding.
 */
const initialDefaults = (): BulkBinding => ({
  tariffName: "",
  contactUuids: [],
  serviceUuids: [],
  autoSelectAllServices: false,
  vehicleTypeUuids: [],
  autoSelectAllVehicleTypes: false,
  tariffZoneType: TariffZoneType.Location,
  tariffType: TariffType.PerHundredPounds,
  rateType: RateType.Flat,
  tariffGroupType: TariffGroupType.Ordinary,
  tariffZoneGroupId: null,
  fuelProfileUuid: null,
  startDate: "2022-01-01",
  endDate: "2030-01-01",
  useActualWeight: true,
});

/**
 * Bulk tariff importer: upload many CSVs, bind them in a grid (seeded from shared
 * defaults), dry-run all, then commit all. Imports target the logged-in company.
 * Reuses the existing `importCrownTariff` mutation per row (looped in the
 * orchestration hook). On production the commit is gated behind a typed "PROD"
 * confirmation and the panel is wrapped in a danger border, mirroring the wizard.
 *
 * Side effects: uploads CSVs to S3 (presigned URLs) and runs the import mutation.
 */
export const BulkTariffImport = () => {
  const { env } = useCrownEnv();
  const isProd = env === "prod";

  const { data: meData } = useMeQuery({ fetchPolicy: "cache-first" });
  const { data: contactsData } = useShallowContactsQuery();
  const { data: servicesData } = useServicesQuery({
    fetchPolicy: "cache-first",
  });
  const { data: vehicleTypesData } = useVehicleTypesQuery({
    fetchPolicy: "cache-first",
  });
  const { data: fuelProfilesData } = useFuelProfilesQuery();
  const { data: tariffZoneGroupsData } = useTariffZoneGroupsQuery({
    variables: { findTariffZoneGroupsInput: { isArchived: false } },
  });

  const companyUuid = meData?.me?.company?.uuid ?? "";
  const companyName = meData?.me?.company?.name ?? "";

  const [defaults, setDefaults] = useState<BulkBinding>(initialDefaults);
  const [typedConfirm, setTypedConfirm] = useState("");

  const {
    rows,
    setRows,
    busy,
    lastSummary,
    setLastSummary,
    counts,
    addFiles,
    removeRow,
    updateBinding,
    dryRunAll,
    importAll,
  } = useBulkTariffImport(companyUuid);

  const contactOptions = useMemo(
    () =>
      contactsData?.contacts?.map((contact) => ({
        value: contact.uuid,
        label: contact.displayName,
      })) ?? [],
    [contactsData?.contacts],
  );
  const serviceOptions = useMemo(
    () =>
      (servicesData?.services ?? [])
        .filter((service) => service.isActive)
        .map((service) => ({ value: service.uuid, label: service.name })),
    [servicesData?.services],
  );
  const vehicleTypeOptions = useMemo(
    () =>
      (vehicleTypesData?.vehicleTypes ?? []).map((vehicleType) => ({
        value: vehicleType.uuid,
        label: vehicleType.name,
      })),
    [vehicleTypesData?.vehicleTypes],
  );

  const contactLabelByUuid = useMemo(
    () => new Map(contactOptions.map((option) => [option.value, option.label])),
    [contactOptions],
  );
  // Groups contact UUIDs by display name so the manifest loader can detect
  // ambiguous names (a label mapping to more than one contact) instead of
  // silently resolving to an arbitrary one.
  const contactUuidsByLabel = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const option of contactOptions) {
      const existing = map.get(option.label) ?? [];
      existing.push(option.value);
      map.set(option.label, existing);
    }
    return map;
  }, [contactOptions]);

  const makeRow = useCallback(
    (file: File): BulkRow => ({
      id: makeRowId(),
      fileName: file.name,
      file,
      tariffUrl: null,
      binding: {
        ...defaults,
        tariffName: defaultNameFromFile(file.name),
        contactUuids: [],
      },
      status: "pending",
      message: null,
      manifestWarning: null,
      tariffsToCreate: null,
      rangesToCreate: null,
    }),
    [defaults],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      addFiles(acceptedFiles, makeRow);
    },
    [addFiles, makeRow],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  });

  const applyDefaultsToAll = useCallback(() => {
    setRows((previous) =>
      previous.map((row) =>
        row.status === "imported"
          ? row
          : {
              ...row,
              binding: {
                ...defaults,
                // Per-row fields are intentionally preserved: applying shared
                // defaults must not wipe name, customers, or the per-row zone
                // type (which varies per tariff, e.g. LOCATION vs MILES).
                tariffName: row.binding.tariffName,
                contactUuids: row.binding.contactUuids,
                tariffZoneType: row.binding.tariffZoneType,
              },
            },
      ),
    );
  }, [defaults, setRows]);

  const exportManifest = useCallback(() => {
    const records = rows.map((row) => rowToRecord(row, contactLabelByUuid));
    const csv = toCsv(records, MANIFEST_COLUMNS);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = MANIFEST_FILENAME;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [rows, contactLabelByUuid]);

  const importManifest = useCallback(
    async (file: File) => {
      const text = await file.text();
      const records = fromCsv(text);
      setRows((previous) => {
        const byFileName = new Map(previous.map((row) => [row.fileName, row]));
        const next = [...previous];
        for (const record of records) {
          const resolution = recordToResolution(
            record,
            defaults,
            contactUuidsByLabel,
          );
          const existing = byFileName.get(resolution.fileName);
          if (isNil(existing)) {
            next.push({
              id: makeRowId(),
              fileName: resolution.fileName,
              file: null,
              tariffUrl: null,
              binding: resolution.binding,
              status: "pending",
              message: "Upload this CSV to enable import",
              manifestWarning: resolution.warning,
              tariffsToCreate: null,
              rangesToCreate: null,
            });
          } else {
            const index = next.indexOf(existing);
            next[index] = {
              ...existing,
              binding: resolution.binding,
              manifestWarning: resolution.warning,
            };
          }
        }
        return next;
      });
    },
    [defaults, contactUuidsByLabel, setRows],
  );

  const onManifestInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!isNil(file)) {
        void importManifest(file);
      }
      // Reset so re-selecting the same file fires change again.
      event.target.value = "";
    },
    [importManifest],
  );

  const needsProdConfirm = isProd && typedConfirm !== PROD_CONFIRM_PHRASE;
  // On prod, require every row to be resolved (dry-run-clean, preview
  // unavailable, or already imported) before any commit, so a partial batch
  // cannot be pushed to production with rows still in error/pending/ready.
  const hasUnresolvedRows = rows.some(
    (row) =>
      row.status !== "dry-run-ok" &&
      row.status !== "dry-run-unavailable" &&
      row.status !== "imported",
  );
  const importDisabled =
    busy ||
    counts.importable === 0 ||
    needsProdConfirm ||
    (isProd && hasUnresolvedRows);

  const content = (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h5">Bulk Tariff Import</Typography>
        <Chip
          color={isProd ? "error" : "default"}
          label={env.toUpperCase()}
          size="small"
        />
        {companyName.length > 0 && (
          <Typography variant="h6">· {companyName}</Typography>
        )}
      </Box>
      <Typography variant="subtitle2" color="textSecondary">
        Imports target the logged-in company. Make sure you are logged in with
        the company account you want these tariffs created for.
      </Typography>

      <Box sx={styles.section}>
        <Typography variant="overline">1 · Upload CSVs</Typography>
        <Box {...getRootProps()} sx={styles.dropzone}>
          <input {...getInputProps()} />
          <Typography variant="body2">
            {isDragActive
              ? "Drop the CSV files here"
              : "Drag tariff CSVs here, or click to select (multiple allowed)"}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography variant="overline">2 · Shared defaults</Typography>
        <BulkDefaultsPanel
          defaults={defaults}
          serviceOptions={serviceOptions}
          vehicleTypeOptions={vehicleTypeOptions}
          fuelProfiles={fuelProfilesData?.fuelProfiles ?? []}
          tariffZoneGroups={tariffZoneGroupsData?.tariffZoneGroups ?? []}
          rowCount={rows.length}
          onChange={(patch) => {
            setDefaults((previous) => ({ ...previous, ...patch }));
          }}
          onApplyToAll={applyDefaultsToAll}
        />
      </Box>

      <Divider sx={styles.section} />

      <Box sx={styles.section}>
        <Typography variant="overline">
          3 · Per-tariff grid (manifest)
        </Typography>
        <Box sx={styles.manifestButtons}>
          <Button
            size="small"
            variant="outlined"
            disabled={rows.length === 0}
            onClick={exportManifest}
          >
            Export manifest
          </Button>
          <Button size="small" variant="outlined" component="label">
            Load manifest
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={onManifestInputChange}
            />
          </Button>
        </Box>
        <BulkGrid
          rows={rows}
          contactOptions={contactOptions}
          busy={busy}
          onUpdateBinding={updateBinding}
          onRemoveRow={removeRow}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography variant="overline">4 · Run</Typography>
        {isProd && (
          <Alert severity="warning" sx={styles.section}>
            You are importing to PRODUCTION. Dry-run all rows, then type
            &quot;PROD&quot; to enable the commit.
          </Alert>
        )}
        <Box sx={styles.actions}>
          <Button
            variant="outlined"
            disabled={busy || counts.total === 0}
            onClick={() => {
              void dryRunAll();
            }}
          >
            Dry-run all
          </Button>
          {isProd && (
            <TextField
              size="small"
              label='Type "PROD" to confirm'
              sx={styles.confirmInput}
              value={typedConfirm}
              onChange={(event) => {
                setTypedConfirm(event.target.value);
              }}
            />
          )}
          <Button
            variant="contained"
            color={isProd ? "error" : "primary"}
            disabled={importDisabled}
            onClick={() => {
              void importAll();
            }}
          >
            Import all ({counts.importable} ready
            {counts.blocked > 0 ? `, ${counts.blocked} blocked` : ""}
            {counts.warned > 0 ? `, ${counts.warned} with warnings` : ""})
          </Button>
          {counts.imported > 0 && (
            <Typography variant="body2" color="success.main">
              {counts.imported} imported (locked)
            </Typography>
          )}
        </Box>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={!isNil(lastSummary)}
        autoHideDuration={6000}
        onClose={() => {
          setLastSummary(null);
        }}
      >
        {isNil(lastSummary) ? undefined : (
          <Alert
            severity={lastSummary.failed > 0 ? "error" : "success"}
            onClose={() => {
              setLastSummary(null);
            }}
          >
            {lastSummary.action === "dry-run" ? "Dry-run" : "Import"} complete:{" "}
            {lastSummary.succeeded} succeeded, {lastSummary.failed} failed
          </Alert>
        )}
      </Snackbar>
    </Box>
  );

  if (isProd) {
    return <Box sx={styles.danger}>{content}</Box>;
  }
  return content;
};

export default BulkTariffImport;
